/**
 * useScaffoldCart — 브라우저 영속 담기 카트 (queue #682 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-14-R682 §R682-2)
 *
 * localStorage `eraf.scaffold.cart` 에 모듈코드 배열(중복 없음 · 정렬 · `^[CG]\d{3}$` 형식만
 * 허용 · 그 외/파싱 실패는 빈 카트로 폴백)을 저장하는 공유 훅. common-showcase
 * (`TakeThisPanel`) · project-scaffold(`ScaffoldWizardPage`/`ScaffoldPreviewStep`) ·
 * getting-started-guide(`CurationBundlePicker`) · `_shared`(`ScaffoldCartBadge`) 4곳이
 * 공유하므로 `_shared/hooks` 에 배치한다(CLAUDE.md §1.2 feature 간 공유).
 *
 * localStorage 의 `storage` 이벤트는 동일 탭에서는 발화되지 않으므로, 모듈-레벨 리스너
 * 집합 + 커스텀 이벤트(`eraf:scaffold-cart`)로 동일 탭 즉시 동기화를 보완한다(타 탭은
 * 네이티브 `storage` 이벤트로 반영). localStorage 미가용(프라이빗 모드 등)이면 메모리
 * 폴백(세션 한정)으로 전환한다.
 */
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'eraf.scaffold.cart';
const CART_EVENT = 'eraf:scaffold-cart';
const CODE_PATTERN = /^[CG]\d{3}$/;
const EMPTY: string[] = [];

const listeners = new Set<() => void>();

let memoryFallback: string[] | null = null;
let cachedRaw: string | null | undefined; // undefined = 아직 미조회
let cachedSnapshot: string[] = EMPTY;

function normalize(codes: readonly string[]): string[] {
  const set = new Set<string>();
  for (const raw of codes) {
    const code = String(raw).trim().toUpperCase();
    if (CODE_PATTERN.test(code)) set.add(code);
  }
  return [...set].sort();
}

function parseStored(raw: string | null): string[] {
  if (!raw) return EMPTY;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    return normalize(parsed as string[]);
  } catch {
    // 손상된 값(비-JSON 등) — 빈 카트로 폴백.
    return EMPTY;
  }
}

function readCart(): string[] {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage 미가용 — 메모리 폴백(참조 안정 — useSyncExternalStore 무한 렌더 방지).
    return memoryFallback ?? EMPTY;
  }
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = parseStored(raw);
  return cachedSnapshot;
}

function writeCart(codes: readonly string[]): void {
  const normalized = normalize(codes);
  const serialized = JSON.stringify(normalized);
  try {
    window.localStorage.setItem(STORAGE_KEY, serialized);
    cachedRaw = serialized;
    cachedSnapshot = normalized;
  } catch {
    // 무음 실패 — 메모리 폴백으로 세션 한정 지속.
    memoryFallback = normalized;
    cachedSnapshot = normalized;
  }
  listeners.forEach((listener) => listener());
  try {
    window.dispatchEvent(new Event(CART_EVENT));
  } catch {
    // 이벤트 디스패치 실패는 무해 — 동일 탭 listener 는 위에서 이미 통지됨.
  }
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  window.addEventListener('storage', onStoreChange);
  window.addEventListener(CART_EVENT, onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener(CART_EVENT, onStoreChange);
  };
}

function getServerSnapshot(): string[] {
  return EMPTY;
}

export interface UseScaffoldCartResult {
  /** 현재 카트(정렬된 모듈코드 배열) */
  codes: string[];
  /** codes.length */
  count: number;
  /** 담김 여부 */
  has: (code: string) => boolean;
  /** 추가(멱등 · UpperCase 정규화) */
  add: (code: string) => void;
  /** 제거(없으면 no-op) */
  remove: (code: string) => void;
  /** 전체 비우기 */
  clear: () => void;
}

export function useScaffoldCart(): UseScaffoldCartResult {
  const codes = useSyncExternalStore(subscribe, readCart, getServerSnapshot);

  const has = useCallback((code: string) => codes.includes(code.trim().toUpperCase()), [codes]);

  const add = useCallback((code: string) => {
    const normalized = code.trim().toUpperCase();
    if (!CODE_PATTERN.test(normalized)) return;
    const current = readCart();
    if (current.includes(normalized)) return;
    writeCart([...current, normalized]);
  }, []);

  const remove = useCallback((code: string) => {
    const normalized = code.trim().toUpperCase();
    const current = readCart();
    if (!current.includes(normalized)) return;
    writeCart(current.filter((c) => c !== normalized));
  }, []);

  const clear = useCallback(() => {
    writeCart(EMPTY);
  }, []);

  return { codes, count: codes.length, has, add, remove, clear };
}
